// =====================================================
// HOOK: usePermissions
// Description: Hook centralisé pour gérer les permissions utilisateur
// =====================================================

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import type { UserRole } from '@/types/user';
import type { EntityType, UserPermissions, EntityPermissions } from '@/types/permissions';
import { PERMISSIONS_CONFIG } from '@/types/permissions';

/**
 * Hook qui retourne les permissions de l'utilisateur connecté
 * Basé sur le tableau "Gestion des droits CRM 4L"
 */
export function usePermissions() {
  const { user } = useAuth();

  const permissions = useMemo<UserPermissions | null>(() => {
    if (!user) return null;

    // Récupérer le rôle depuis les métadonnées
    const userRole = (user.app_metadata?.role || 'user') as UserRole;

    // Récupérer la config des permissions pour ce rôle
    const rolePermissions = PERMISSIONS_CONFIG[userRole];

    return {
      role: userRole,
      ...rolePermissions,
    };
  }, [user]);

  // Fonction helper pour obtenir les permissions d'une entité
  const getEntityPermissions = (entity: EntityType): EntityPermissions | null => {
    if (!permissions) return null;

    const entityMap: Record<
      EntityType,
      keyof Omit<
        UserPermissions,
        'role' | 'canManageUsers' | 'canViewDashboard' | 'canAccessAllData'
      >
    > = {
      contact: 'contacts',
      crew: 'crews',
      product: 'products',
      vehicle: 'vehicles',
      school: 'schools',
      association: 'associations',
      discount: 'discounts',
      form: 'forms', // Formulaires (templates)
      prospect: 'prospects', // Prospects (form_submissions)
      timeline: 'timeline',
      task: 'tasks',
      user: 'contacts', // Pas de permissions spécifiques, utiliser contacts par défaut
    };

    const permissionKey = entityMap[entity];
    return permissions[permissionKey] || null;
  };

  // Fonctions de vérification de permissions

  const canManageUsers = (): boolean => {
    return permissions?.canManageUsers || false;
  };

  const canViewDashboard = (): boolean => {
    return permissions?.canViewDashboard || false;
  };

  const canAccessAllData = (): boolean => {
    return permissions?.canAccessAllData || false;
  };

  const canViewEntity = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.view || false;
  };

  const canViewAllData = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.viewAll || false;
  };

  const canCreateEntity = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.create || false;
  };

  const canUpdateEntity = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.update || false;
  };

  const canDeleteEntity = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.delete || false;
  };

  const canImportData = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.import || false;
  };

  const canExportData = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.export || false;
  };

  const canDeleteBulk = (entity: EntityType): boolean => {
    const entityPerms = getEntityPermissions(entity);
    return entityPerms?.deleteBulk || false;
  };

  /**
   * Vérifie si l'utilisateur peut gérer les assignations (voir et modifier assigned_to)
   * Uniquement admin et superadmin peuvent voir/gérer les assignations
   * Les users ont l'auto-assignation via trigger, donc pas besoin de voir le champ
   */
  const canManageAssignments = (): boolean => {
    return permissions?.role === 'admin' || permissions?.role === 'superadmin';
  };

  /**
   * Vérifie si l'utilisateur peut modifier les champs sensibles d'un équipage
   * (statut, réduction, numéro d'équipage)
   * Uniquement admin et superadmin peuvent modifier ces champs
   */
  const canEditCrewSensitiveFields = (): boolean => {
    return permissions?.role === 'admin' || permissions?.role === 'superadmin';
  };

  return {
    permissions,
    role: permissions?.role || 'user',

    // Permissions générales
    canManageUsers,
    canViewDashboard,
    canAccessAllData,
    canManageAssignments,
    canEditCrewSensitiveFields,

    // Permissions par entité
    canViewEntity,
    canViewAllData,
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canDeleteBulk,

    // Helper pour obtenir toutes les permissions d'une entité
    getEntityPermissions,
  };
}
